/**
 * The Logic tab — branching as an ordered list, not a canvas.
 *
 * Visual Reference: `design/screens/editor-logic.html` — default (four rules,
 * one carrying the never-runs warning), loading, empty, error.
 *
 * PRD §3.4 scopes v1 to a "**List-based conditional logic editor**" against the
 * M6 "Full React Flow logic canvas", and §7.5 repeats it: "Logic | Branching and
 * visibility. List-based editor in v1; React Flow canvas in M6."
 *
 * THE SENTENCE ON THIS SCREEN IS NOT THE MOCKUP'S. The export says "Rules run
 * in order. The first matching rule wins." DECISION-034 made that incomplete —
 * `logic[]` is evaluated per step, so it must read "Rules run in order **within
 * each step**. The first matching rule wins." The decision itself says the copy
 * change flows through Claude Design → `design/imports/` → `/design ingest`,
 * and that it had not been made yet; Story 6.4's own criterion says to build to
 * the corrected sentence and say so. This is saying so. The mockup is unedited.
 *
 * Evaluation order is IMPORTED, never re-derived — `logicGroupOf` from
 * `packages/schema`, through `editor/reachability.ts`. PRD §3.4 calls
 * builder/runtime divergence "the defining bug class of every form builder ever
 * shipped", and a second copy of the order in this file is how it starts.
 *
 * Story 6.4.
 */
import type { Block, Funnel, LogicRule } from '@leadgible/schema'
import { BLOCK_LABEL, hasOptions, newBlockId } from './blocks'
import { groupedRules, reachability } from './reachability'
import { edit, useEditor } from './store'
import { duplicateRule, insertRule } from './vocabulary'
import { Pill } from '../ui/Pill'

/** DECISION-034's sentence, corrected. See the header. */
export const EVALUATION_CONTRACT =
  'Rules run in order within each step. The first matching rule wins.'

/** The four kinds the export's chooser offers, with its own one-line copy. */
export const RULE_KINDS = [
  {
    id: 'show_hide',
    label: 'Show or hide a question',
    what: 'Ask follow-ups only of the people they apply to.',
  },
  { id: 'skip_to', label: 'Skip to a step', what: 'Send someone straight to the step that matters.' },
  {
    id: 'disqualify',
    label: 'Disqualify immediately',
    what: 'End the funnel early and send the guide instead.',
  },
  {
    id: 'set_hidden_field',
    label: 'Set a hidden field',
    what: 'Tag the lead or route it to an owner on the way through.',
  },
] as const
export type RuleKind = (typeof RULE_KINDS)[number]['id']

/** The five actions, in the export's own words. */
const ACTION_LABEL: Record<string, string> = {
  show: 'show',
  hide: 'hide',
  skip_to: 'skip to',
  disqualify: 'disqualify',
  set_hidden_field: 'set',
}

export function LogicTab() {
  const funnel = useEditor((s) => s.funnel)
  if (!funnel) return null

  const groups = groupedRules(funnel)
  const questions = funnel.blocks.filter((b) => 'question' in b)

  return (
    <div className="flex justify-center px-mdx-24 pb-mdx-48 pt-mdx-28">
      {/*
        The export caps this column at 820px and no token holds that value —
        Q-041. `--mdx-measure` is the default reading measure, which is what a
        centred column of sentences actually is, so it is the substitute with
        the right MEANING rather than the nearest number.
      */}
      <div className="flex w-full max-w-mdx-measure flex-col gap-mdx-24">
        <header className="flex flex-wrap items-end justify-between gap-x-mdx-20 gap-y-mdx-14">
          <div className="flex min-w-0 flex-col gap-mdx-6">
            <span className="text-mdx-12 font-mdx-bold uppercase tracking-mdx-eyebrow text-mdx-slate">
              Logic
            </span>
            <h2 className="m-0 text-mdx-25 font-mdx-bold leading-mdx-heading tracking-mdx-h1">
              Rules
            </h2>
            <p className="m-0 max-w-mdx-measure-56 text-mdx-15 text-mdx-slate">
              {EVALUATION_CONTRACT}
            </p>
          </div>
          <AddRuleButton funnel={funnel} disabled={questions.length === 0} />
        </header>

        {funnel.logic.length === 0 ? (
          <Empty funnel={funnel} canAdd={questions.length > 0} />
        ) : (
          <div className="flex flex-col gap-mdx-28">
            {groups.map((group) => (
              <section key={group.key} className="flex flex-col gap-mdx-12">
                <div className="flex items-baseline gap-mdx-12">
                  <span className="flex-none text-mdx-17 font-mdx-semibold tracking-mdx-h3">
                    {group.title}
                  </span>
                  <span className="flex-none text-mdx-13 text-mdx-slate">
                    {group.rules.length} {group.rules.length === 1 ? 'rule' : 'rules'}
                  </span>
                  <span aria-hidden="true" className="h-px flex-1 bg-mdx-line-soft" />
                </div>
                {group.rules.map((rule, index) => (
                  <RuleCard
                    key={rule.id}
                    funnel={funnel}
                    rule={rule}
                    // Numbered WITHIN the group, because the group is what the
                    // first-match contract is scoped to (DECISION-034).
                    number={index + 1}
                  />
                ))}
              </section>
            ))}

            <Chooser funnel={funnel} disabled={questions.length === 0} />
          </div>
        )}
      </div>
    </div>
  )
}

/* ── a rule ─────────────────────────────────────────────────────────────── */

function RuleCard({ funnel, rule, number }: { funnel: Funnel; rule: LogicRule; number: number }) {
  const verdict = reachability(funnel, rule)
  const questions = funnel.blocks.filter((b): b is Block => 'question' in b)
  const subject = rule.when.subject
  const source = subject.kind === 'answer' ? subject.block_id : ''
  const sourceBlock = funnel.blocks.find((b) => b.id === source)

  const update = (label: string, apply: (draft: LogicRule) => void) =>
    edit(label, (draft) => {
      const target = draft.logic.find((r) => r.id === rule.id)
      if (target) apply(target)
    })

  return (
    <article className="flex flex-col gap-mdx-14 rounded-mdx-card border border-mdx-line bg-mdx-white px-mdx-20 py-mdx-18 shadow-mdx-card">
      <div className="flex flex-wrap items-center gap-x-mdx-12 gap-y-mdx-10">
        <span className="flex-none text-mdx-12 font-mdx-bold uppercase tracking-mdx-eyebrow text-mdx-slate">
          Rule {number}
        </span>
        {verdict.ok ? null : (
          <Pill tone="review" dot>
            Never runs
          </Pill>
        )}
        <span className="ml-auto flex flex-none gap-mdx-8">
          <RuleButton
            onClick={() => {
              // The vocabulary's `add_branch` (Story 27.2): a copy, straight after it.
              const id = newBlockId().replace('blk_', 'lgc_')
              edit('Duplicate a rule', (draft) => duplicateRule(draft, rule.id, id))
            }}
          >
            Duplicate
          </RuleButton>
          <RuleButton
            danger
            onClick={() =>
              edit('Delete a rule', (draft) => {
                draft.logic = draft.logic.filter((r) => r.id !== rule.id)
              })
            }
          >
            Delete
          </RuleButton>
        </span>
      </div>

      {/* If {question} is {answer} then {action} {target} — the export's sentence. */}
      <div className="flex flex-wrap items-center gap-mdx-8 text-mdx-15">
        <span className="text-mdx-slate">If</span>
        <Select
          label="Question"
          value={source}
          onChange={(next) => update('Change what a rule asks about', (draft) => {
            draft.when.subject = { kind: 'answer', block_id: next }
            // The old value belonged to the old question's options and would
            // silently never match.
            draft.when.value = ''
          })}
          options={questions.map((block) => ({
            value: block.id,
            label: 'question' in block ? block.question : block.id,
          }))}
        />
        <span className="text-mdx-slate">is</span>
        <Select
          label="Answer"
          value={typeof rule.when.value === 'string' ? rule.when.value : ''}
          onChange={(next) => update('Change what a rule matches', (draft) => {
            draft.when.value = next
          })}
          options={
            sourceBlock && hasOptions(sourceBlock.type) && 'options' in sourceBlock
              ? sourceBlock.options.map((option) => ({ value: option.id, label: option.label }))
              : []
          }
          empty="anything"
        />
        <span className="text-mdx-slate">then</span>
        <Select
          label="Action"
          value={rule.then.action}
          onChange={(next) => update('Change what a rule does', (draft) => {
            draft.then = effectFor(next, funnel)
          })}
          options={Object.keys(ACTION_LABEL).map((action) => ({
            value: action,
            label: ACTION_LABEL[action]!,
          }))}
        />
        <Target funnel={funnel} rule={rule} update={update} />
      </div>

      {verdict.ok ? null : (
        <div className="flex items-start gap-mdx-16 rounded-mdx-control bg-mdx-review-tint px-mdx-16 py-mdx-12">
          <span
            aria-hidden="true"
            className="flex size-mdx-evidence-icon flex-none items-center justify-center rounded-mdx-control-sm bg-mdx-review text-mdx-14 font-mdx-bold text-mdx-white"
          >
            !
          </span>
          <span className="min-w-0 text-mdx-15">
            <span className="font-mdx-semibold">This rule can never run</span>
            <span className="text-mdx-review-ink"> — {verdict.reason}</span>
          </span>
        </div>
      )}
    </article>
  )
}

/** The action's own second half: which block, which step, which field. */
function Target({
  funnel,
  rule,
  update,
}: {
  funnel: Funnel
  rule: LogicRule
  update: (label: string, apply: (draft: LogicRule) => void) => void
}) {
  const effect = rule.then

  if (effect.action === 'show' || effect.action === 'hide') {
    return (
      <Select
        label={effect.action === 'show' ? 'Question to show' : 'Question to hide'}
        value={effect.target_block_id}
        onChange={(next) =>
          update('Change a rule’s target', (draft) => {
            if (draft.then.action === 'show' || draft.then.action === 'hide') {
              draft.then.target_block_id = next
            }
          })
        }
        options={funnel.blocks.map((block) => ({
          value: block.id,
          label: 'question' in block ? block.question : BLOCK_LABEL[block.type],
        }))}
      />
    )
  }

  if (effect.action === 'skip_to') {
    return (
      <Select
        label="Step to skip to"
        value={effect.target_step_id}
        onChange={(next) =>
          update('Change a rule’s target', (draft) => {
            if (draft.then.action === 'skip_to') draft.then.target_step_id = next
          })
        }
        options={funnel.steps.map((step, index) => ({
          value: step.id,
          label: step.title ?? `Step ${index + 1}`,
        }))}
      />
    )
  }

  if (effect.action === 'set_hidden_field') {
    // The effect names the hidden-field BLOCK, not its key — the key can be
    // renamed on the Build tab and a rule pointing at a string would quietly
    // stop matching anything.
    const hidden = funnel.blocks.filter(
      (block): block is Extract<Block, { type: 'hidden_field' }> =>
        block.type === 'hidden_field',
    )
    return (
      <Select
        label="Hidden field"
        value={effect.target_block_id}
        onChange={(next) =>
          update('Change a rule’s target', (draft) => {
            if (draft.then.action === 'set_hidden_field') draft.then.target_block_id = next
          })
        }
        options={hidden.map((block) => ({ value: block.id, label: block.field_key }))}
        empty="no hidden fields yet"
      />
    )
  }

  // `disqualify` takes no target — the funnel ends. RULE-027: the respondent
  // still reaches the outcome page, and the lead is still stored and routed.
  return <span className="text-mdx-slate">and send the guide</span>
}

/* ── adding one ─────────────────────────────────────────────────────────── */

/** The default effect for a kind, valid the moment it exists. */
function effectFor(action: string, funnel: Funnel): LogicRule['then'] {
  const firstBlock = funnel.blocks[0]?.id ?? ''
  switch (action) {
    case 'hide':
      return { action: 'hide', target_block_id: firstBlock }
    case 'skip_to':
      return { action: 'skip_to', target_step_id: funnel.steps[funnel.steps.length - 1]?.id ?? '' }
    case 'disqualify':
      return { action: 'disqualify' }
    case 'set_hidden_field':
      return {
        action: 'set_hidden_field',
        target_block_id: funnel.blocks.find((b) => b.type === 'hidden_field')?.id ?? '',
        value: '',
      }
    default:
      return { action: 'show', target_block_id: firstBlock }
  }
}

function addRule(funnel: Funnel, kind: RuleKind) {
  const question = funnel.blocks.find((b) => 'question' in b)
  if (!question) return
  const stepId = funnel.steps.find((s) => s.blocks.includes(question.id))?.id
  const action = kind === 'show_hide' ? 'show' : kind
  const id = newBlockId().replace('blk_', 'lgc_')

  // The vocabulary's `add_branch` (Story 27.2). A rule whose effect names nothing
  // — "Set a hidden field" on a funnel with none — is refused there and adds
  // nothing, where it used to add a rule the save refused (BACKLOG, 2026-09-29).
  edit(`Add a rule — ${kind.replace(/_/g, ' ')}`, (draft) => {
    insertRule(draft, {
      id,
      ...(stepId ? { step_id: stepId } : {}),
      when: {
        subject: { kind: 'answer', block_id: question.id },
        operator: 'is',
        value: 'options' in question ? (question.options[0]?.id ?? '') : '',
      },
      then: effectFor(action, draft),
    })
  })
}

function AddRuleButton({ funnel, disabled }: { funnel: Funnel; disabled: boolean }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => addRule(funnel, 'show_hide')}
      className="flex-none rounded-mdx-control border-0 bg-mdx-brand px-mdx-24 py-mdx-12 text-mdx-15 font-mdx-semibold text-mdx-white disabled:cursor-not-allowed disabled:opacity-mdx-recessed"
    >
      Add rule
    </button>
  )
}

/** The export's 2×2 chooser, each kind with its one line. */
function Chooser({ funnel, disabled }: { funnel: Funnel; disabled: boolean }) {
  return (
    <section className="flex flex-col gap-mdx-12">
      <div className="flex items-baseline gap-mdx-12">
        <span className="flex-none text-mdx-12 font-mdx-bold uppercase tracking-mdx-eyebrow text-mdx-slate">
          Add rule
        </span>
        <span aria-hidden="true" className="h-px flex-1 bg-mdx-line-soft" />
      </div>
      <div className="grid grid-cols-1 gap-mdx-4 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-10 shadow-mdx-card sm:grid-cols-2">
        {RULE_KINDS.map((kind) => (
          <button
            key={kind.id}
            type="button"
            disabled={disabled}
            onClick={() => addRule(funnel, kind.id)}
            className="flex flex-col gap-mdx-2 rounded-mdx-control border-0 bg-transparent px-mdx-14 py-mdx-12 text-left transition-colors ease-mdx-swift hover:bg-mdx-ground disabled:cursor-not-allowed disabled:opacity-mdx-recessed"
          >
            <span className="text-mdx-15 font-mdx-semibold text-mdx-ink">{kind.label}</span>
            <span className="text-mdx-13 text-mdx-slate">{kind.what}</span>
          </button>
        ))}
      </div>
    </section>
  )
}

function Empty({ funnel, canAdd }: { funnel: Funnel; canAdd: boolean }) {
  return (
    <div className="flex flex-col items-center gap-mdx-14 rounded-mdx-card border border-mdx-line bg-mdx-white px-mdx-28 py-mdx-48 text-center shadow-mdx-card">
      <span aria-hidden="true" className="size-mdx-40 flex-none rounded-mdx-control bg-mdx-line" />
      <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">
        No rules yet — every respondent sees every question
      </span>
      <span className="max-w-mdx-measure-52 text-mdx-15 text-mdx-slate">
        Plenty of funnels never need a rule. Add one when you want to skip a step, ask a follow-up
        of some people only, or end the funnel early.
      </span>
      {canAdd ? (
        <button
          type="button"
          onClick={() => addRule(funnel, 'show_hide')}
          className="mt-mdx-2 cursor-pointer rounded-mdx-control border-0 bg-mdx-brand px-mdx-24 py-mdx-12 text-mdx-15 font-mdx-semibold text-mdx-white"
        >
          Add a rule
        </button>
      ) : (
        // A rule needs something to ask about. Saying so beats a button that
        // does nothing when pressed.
        <span className="text-mdx-13 text-mdx-slate">
          Add a question on the Build tab first — a rule needs an answer to look at.
        </span>
      )}
    </div>
  )
}

/* ── the export's select ────────────────────────────────────────────────── */

function Select({
  label,
  value,
  onChange,
  options,
  empty,
}: {
  label: string
  value: string
  onChange: (next: string) => void
  options: { value: string; label: string }[]
  empty?: string
}) {
  return (
    <span className="relative inline-flex">
      <select
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={options.length === 0}
        className="cursor-pointer appearance-none rounded-mdx-control border border-mdx-line bg-mdx-white py-mdx-8 pl-mdx-12 pr-mdx-32 text-mdx-15 font-mdx-semibold text-mdx-ink outline-none focus:border-mdx-brand disabled:cursor-not-allowed disabled:opacity-mdx-recessed"
      >
        {options.length === 0 ? <option value="">{empty ?? '—'}</option> : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-mdx-12 top-mdx-9 text-mdx-12 text-mdx-slate"
      >
        ▾
      </span>
    </span>
  )
}

function RuleButton({
  children,
  onClick,
  danger = false,
}: {
  children: React.ReactNode
  onClick: () => void
  danger?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'cursor-pointer rounded-mdx-control-sm border border-mdx-line bg-mdx-white px-mdx-16 py-mdx-8',
        'text-mdx-13 font-mdx-semibold transition-colors ease-mdx-swift hover:bg-mdx-ground',
        danger ? 'text-mdx-error' : 'text-mdx-ink',
      ].join(' ')}
    >
      {children}
    </button>
  )
}
